import { coreQueryKey } from '@/api/core/authorization';
import { readDeletedNativeProperties, readNativeProperties } from '@/api/core/native-property-read';
import { isApiError } from '@/api/http/errors';
import { useQuery } from '@tanstack/react-query';
import { propertyRead, type PropertyRuntime } from './property-queries';

/**
 * 지점 목록 두 보기의 조회. 한 번에 한 보기만 보이므로 보이지 않는 보기는 `enabled: false`로 쉬게 한다
 * (다시 돌아오면 이미 받은 목록을 보여 둔 채 새로 받는다). 두 목록은 cursor와 응답이 서로 독립이다.
 */
interface ListQueryInput {
  runtime: PropertyRuntime;
  generation: number;
  enabled: boolean;
}

/** 운영 지점 목록 — 지점명 정렬 방향과 서버가 준 opaque cursor로만 읽는다. */
export function useNativePropertyList({
  runtime,
  generation,
  enabled,
  cursor,
  direction,
}: ListQueryInput & { cursor: string | null; direction: 'asc' | 'desc' }) {
  return useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'native-properties'),
      direction,
      cursor,
    ],
    queryFn: async ({ signal }) => {
      try {
        return await readNativeProperties(runtime.client, cursor, direction, signal);
      } catch (error) {
        if (
          !signal.aborted &&
          isApiError(error) &&
          error.status === 403 &&
          runtime.session.getSnapshot().generation === generation
        )
          runtime.session.reportDomainAccessDenied('property', generation);
        throw error;
      }
    },
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });
}

/** Core에서 삭제된 지점 목록(읽기 전용) — 자체 cursor(`deleted_cursor`)를 쓰고 항상 오름차순이다. */
export function useDeletedNativeProperties({
  runtime,
  generation,
  enabled,
  cursor,
}: ListQueryInput & { cursor: string | null }) {
  return useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'native-properties'),
      'deleted',
      cursor,
    ],
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        generation,
        () => readDeletedNativeProperties(runtime.client, cursor, 'asc', signal),
        signal,
        () => runtime.session.getSnapshot().generation === generation,
      ),
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });
}
